/**
 * Balance Sheet Panel Component
 *
 * Spreadsheet-grade entry interface for Balance Sheet data.
 * Driven by BS_STRUCTURE config for multi-level category hierarchy.
 *
 * Supports:
 * - Detailed mode (~53 items with subcategory subheaders)
 * - Simplified mode (~8 calculated totals)
 * - Real-time balance validation (Assets = Liabilities + Equity)
 * - Multi-period column layout
 * - SMSF template fallback
 *
 * Sprint 14, F004: BS Panel Rewrite with Multi-Level Sections
 */

import { tryBalanceSheetTotals, hasUnrepresentedBalanceSheetLines, BS_CUSTOM_CATEGORIES } from '../../../../../shared/balance-sheet-validation';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, RotateCw } from 'lucide-react';
import { FinancialSpreadsheet } from '../spreadsheet/financial-spreadsheet';
import { useValidateBalanceSheet, useCustomLineItems, useUpsertCustomLineItemValue, useDeleteCustomLineItem } from '@/hooks/use-financial-data';
import { updateBSLineItem, createBSLineItem, getBalanceSheet } from '@/lib/api/financial';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AddCustomLineItemDialog } from '../modals/add-custom-line-item-modal';
import {
  BS_STRUCTURE,
  getSimplifiedBSRows,
  type BSCategoryConfig,
} from '@/lib/financial/bs-category-config';
import type { SpreadsheetConfig, SpreadsheetSection, SpreadsheetRow, PeriodColumn } from '../spreadsheet/types';
import type { BalanceSheetLineItem, BalanceSheetValidation, BSLineItemType, CustomLineItemBSCategory } from '@/types/financial';

/** Map BS category key → custom line item category key */
const CATEGORY_TO_CUSTOM = BS_CUSTOM_CATEGORIES as Record<string, CustomLineItemBSCategory>;

/** SMSF-specific line items (legacy fallback) */
const SMSF_ITEMS: { id: BSLineItemType; label: string }[] = [
  { id: 'cash_holdings_smsf', label: 'Cash Holdings (SMSF)' },
  { id: 'property_values_smsf', label: 'Property Values (SMSF)' },
  { id: 'other_assets_smsf', label: 'Other Assets (SMSF)' },
  { id: 'loan_balance_smsf', label: 'Loan Balance (SMSF)' },
];

function balanceSheetInputKey(
  selectedPeriodId: string,
  items: BalanceSheetLineItem[],
  custom: ReturnType<typeof useCustomLineItems>['data'],
): string {
  // Match the grid's last standard row / first custom value lookup. Ignore metadata and row order.
  const standard = Array.from(new Map(items.map(item => [item.lineItem, Number(item.amount)])).entries())
    .filter(([, amount]) => amount !== 0).sort(([a], [b]) => a.localeCompare(b));
  const extra = (custom?.definitions ?? []).map(definition => [definition.id, definition.category,
    Number(custom?.values.find(value => value.customLineItemId === definition.id && value.financialPeriodId === selectedPeriodId)?.amount ?? 0)] as const)
    .filter(([, , amount]) => amount !== 0).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify([selectedPeriodId, standard, extra]);
}

export interface BalanceSheetPanelProps {
  periodId: string | undefined;
  periods?: PeriodColumn[];
  mode?: 'simplified' | 'detailed';
  readOnly?: boolean;
  entityRecordId?: string;
  financialTemplate?: string;
}

export const BalanceSheetPanel: React.FC<BalanceSheetPanelProps> = ({
  periodId,
  periods: periodsProp,
  mode: modeProp = 'simplified',
  readOnly = false,
  entityRecordId,
  financialTemplate,
}) => {
  const { mutateAsync: validateBalanceSheet, isPending: isValidating } = useValidateBalanceSheet();
  const [validationState, setValidationState] = useState<Record<string, {
    inputKey: string; result: BalanceSheetValidation | null; failed: boolean;
  }>>({});
  const attemptedInputs = useRef(new Set<string>());
  const requestSequences = useRef(new Map<string, number>());
  const refreshingPeriods = useRef(new Set<string>());

  // Custom line items
  const customQuery = useCustomLineItems(entityRecordId, 'balance_sheet');
  const { data: customData } = customQuery;
  const customDefinitions = customData?.definitions || [];
  const customValues = customData?.values || [];
  const upsertValueMutation = useUpsertCustomLineItemValue();
  const deleteCustomMutation = useDeleteCustomLineItem();

  // Add dialog state (wired in F005 for per-section add)
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogCategory, setAddDialogCategory] = useState<CustomLineItemBSCategory>('current_asset');
  const [addDialogCategoryLabel, setAddDialogCategoryLabel] = useState('Current Assets');

  // Default periods if not provided
  const defaultPeriods: PeriodColumn[] = useMemo(() => {
    if (periodsProp && periodsProp.length > 0) {
      return periodsProp;
    }
    return [
      {
        periodId: periodId || 'fy2025',
        label: 'FY2025',
        isAnnualised: false,
        trustGrade: 'credit_grade' as const,
      },
    ];
  }, [periodsProp, periodId]);

  const bsQueries = useQueries({
    queries: defaultPeriods.map(period => ({
      queryKey: ['financial', 'balance-sheet', period.periodId],
      queryFn: () => getBalanceSheet(period.periodId),
      enabled: !!periodId,
    })),
  });
  const queryClient = useQueryClient();
  const isLoading = bsQueries.some(query => query.isLoading) || customQuery.isLoading;
  const readFailed = bsQueries.some(query => query.isError) || customQuery.isError;
  const lineItems = bsQueries[defaultPeriods.findIndex(period => period.periodId === periodId)]?.data ?? [];
  const refetch = useCallback(
    async (selectedPeriodId?: string) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['financial', 'balance-sheet', ...(selectedPeriodId ? [selectedPeriodId] : [])] }),
        queryClient.invalidateQueries({ queryKey: ['financial', 'custom-line-items', entityRecordId, 'balance_sheet'] }),
      ]);
      const periods = selectedPeriodId ? [selectedPeriodId] : defaultPeriods.map(period => period.periodId);
      if (periods.some(id => queryClient.getQueryState(['financial', 'balance-sheet', id])?.status === 'error') ||
        queryClient.getQueryState(['financial', 'custom-line-items', entityRecordId, 'balance_sheet'])?.status === 'error') {
        throw new Error('Balance Sheet read failed');
      }
    },
    [queryClient, defaultPeriods, entityRecordId]
  );

  const customDataRef = useRef(customData);
  customDataRef.current = customData;
  const getInputKey = useCallback((selectedPeriodId: string) => {
    const items = queryClient.getQueryData<BalanceSheetLineItem[]>(['financial', 'balance-sheet', selectedPeriodId]) ?? [];
    const custom = queryClient.getQueryData<typeof customData>(['financial', 'custom-line-items', entityRecordId, 'balance_sheet']) ?? customDataRef.current;
    return balanceSheetInputKey(selectedPeriodId, items, custom);
  }, [queryClient, entityRecordId]);
  const inputKey = periodId ? balanceSheetInputKey(periodId, lineItems, customData) : '';
  const currentState = periodId && validationState[periodId]?.inputKey === inputKey ? validationState[periodId] : undefined;
  const validation = currentState?.result;
  const validationFailed = currentState?.failed;

  // Reserve a sequence before refresh so an older reply cannot restore an old result.
  const beginValidation = useCallback((selectedPeriodId: string) => {
    const sequence = (requestSequences.current.get(selectedPeriodId) ?? 0) + 1;
    requestSequences.current.set(selectedPeriodId, sequence);
    setValidationState(previous => ({ ...previous, [selectedPeriodId]: { inputKey: getInputKey(selectedPeriodId), result: null, failed: false } }));
    return sequence;
  }, [getInputKey]);
  const validationError = useCallback((selectedPeriodId: string, sequence: number, key = getInputKey(selectedPeriodId)) => {
    if (requestSequences.current.get(selectedPeriodId) === sequence) {
      attemptedInputs.current.add(key);
      setValidationState(previous => ({ ...previous, [selectedPeriodId]: { inputKey: key, result: null, failed: true } }));
    }
  }, [getInputKey]);
  const validatePeriod = useCallback(async (selectedPeriodId: string, sequence = beginValidation(selectedPeriodId)) => {
    if (requestSequences.current.get(selectedPeriodId) !== sequence) return;
    const key = getInputKey(selectedPeriodId);
    attemptedInputs.current.add(key);
    try {
      const result = await validateBalanceSheet(selectedPeriodId);
      if (requestSequences.current.get(selectedPeriodId) === sequence) {
        setValidationState(previous => ({ ...previous, [selectedPeriodId]: { inputKey: key, result, failed: false } }));
      }
    } catch {
      validationError(selectedPeriodId, sequence, key);
    }
  }, [validateBalanceSheet, beginValidation, validationError, getInputKey]);

  // Build line item lookup map
  const lineItemMap = useMemo(() => {
    const map = new Map<string, BalanceSheetLineItem>();
    bsQueries.forEach((query, index) => {
      query.data?.forEach(item => map.set(`${defaultPeriods[index].periodId}:${item.lineItem}`, item));
    });
    return map;
  }, [bsQueries, defaultPeriods]);

  // Get value for a line item and period
  const getValue = useCallback(
    (lineItemType: BSLineItemType, selectedPeriodId: string): number => {
      const item = lineItemMap.get(`${selectedPeriodId}:${lineItemType}`);
      return item?.amount || 0;
    },
    [lineItemMap]
  );

  // Build values map for a single line item across all periods
  const buildValuesMap = useCallback(
    (lineItemType: BSLineItemType): Record<string, number> => {
      const values: Record<string, number> = {};
      defaultPeriods.forEach((period) => {
        values[period.periodId] = getValue(lineItemType, period.periodId);
      });
      return values;
    },
    [defaultPeriods, getValue]
  );

  // Zero values map for subheader/display rows
  const zeroValues = useCallback((): Record<string, number> => {
    const values: Record<string, number> = {};
    defaultPeriods.forEach((period) => {
      values[period.periodId] = 0;
    });
    return values;
  }, [defaultPeriods]);

  // Custom value lookup helper
  const getCustomValue = useCallback(
    (customLineItemId: string, pId: string): number => {
      const val = customValues.find(
        (v: any) => v.customLineItemId === customLineItemId && v.financialPeriodId === pId
      );
      return val ? Number(val.amount) : 0;
    },
    [customValues]
  );

  // Build custom rows for a category
  const buildCustomRows = useCallback(
    (category: string): SpreadsheetRow[] => {
      return customDefinitions
        .filter((d: any) => d.category === category)
        .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0) || a.label.localeCompare(b.label))
        .map((d: any) => ({
          id: `custom_${d.id}`,
          label: d.label,
          type: 'input' as const,
          isCustom: true,
          customLineItemId: d.id,
          values: defaultPeriods.reduce(
            (acc: Record<string, number>, period) => {
              acc[period.periodId] = getCustomValue(d.id, period.periodId);
              return acc;
            },
            {} as Record<string, number>
          ),
        }));
    },
    [customDefinitions, defaultPeriods, getCustomValue]
  );

  // The grid and server use the same lines, signs and cent arithmetic.
  const totalsByPeriod = useMemo(() => Object.fromEntries(defaultPeriods.map(period => [
    period.periodId,
    tryBalanceSheetTotals(
      Array.from(lineItemMap.values()).filter(item => item.financialPeriodId === period.periodId),
      customDefinitions.map(definition => ({
        category: definition.category,
        amount: getCustomValue(definition.id, period.periodId),
      })),
    ),
  ])), [defaultPeriods, lineItemMap, customDefinitions, getCustomValue]);

  const buildTotalValues = useCallback((key: string): Record<string, number | null> =>
    Object.fromEntries(defaultPeriods.map(period => [period.periodId, totalsByPeriod[period.periodId]?.[key] ?? null])),
  [defaultPeriods, totalsByPeriod]);

  const calculateGridTotal = useCallback((key: string, values: Record<string, number>) => tryBalanceSheetTotals(
    Object.entries(values).map(([lineItem, amount]) => ({ lineItem, amount })),
    customDefinitions.map(definition => ({ category: definition.category, amount: values[`custom_${definition.id}`] ?? 0 })),
  )?.[key] ?? null, [customDefinitions]);

  // Handle cell change - persist via API
  const handleCellChange = useCallback(
    async (rowId: string, selectedPeriodId: string, value: number) => {
      if (!defaultPeriods.some(period => period.periodId === selectedPeriodId)) {
        throw new Error('Select a loaded financial period before editing.');
      }
      // Handle custom line items
      if (rowId.startsWith('custom_')) {
        const customLineItemId = rowId.replace('custom_', '');
        if (!entityRecordId) throw new Error('Select a loaded financial record before editing.');

        await upsertValueMutation.mutateAsync({
          customLineItemId,
          financialPeriodId: selectedPeriodId,
          amount: value,
          entityRecordId,
          statementType: 'balance_sheet',
        });

        refreshingPeriods.current.add(selectedPeriodId);
        const sequence = beginValidation(selectedPeriodId);
        try {
          await refetch();
          refreshingPeriods.current.delete(selectedPeriodId);
          await validatePeriod(selectedPeriodId, sequence);
        } catch (error) {
          validationError(selectedPeriodId, sequence);
          console.error('Balance Sheet saved; refresh or validation failed:', error);
        } finally {
          refreshingPeriods.current.delete(selectedPeriodId);
        }
        return;
      }

      // Handle standard line items
      const item = lineItemMap.get(`${selectedPeriodId}:${rowId}`);
      if (item) {
        await updateBSLineItem(item.id, {
          amount: value,
          expectedUpdatedAt: item.updatedAt,
          manualAdjustmentReasonCode: 'source_correction',
          manualAdjustmentReasonText: 'Manual Balance Sheet line-item edit',
        });
      } else {
        await createBSLineItem({
          financialPeriodId: selectedPeriodId, lineItem: rowId, amount: value,
          manualAdjustmentReasonCode: 'source_correction',
          manualAdjustmentReasonText: 'Manual Balance Sheet line-item edit',
        });
      }

      // The figure is stored. A refresh or validation error must not report a failed save.
      refreshingPeriods.current.add(selectedPeriodId);
      const sequence = beginValidation(selectedPeriodId);
      try {
        await refetch(selectedPeriodId);
        refreshingPeriods.current.delete(selectedPeriodId);
        await validatePeriod(selectedPeriodId, sequence);
      } catch (error) {
        validationError(selectedPeriodId, sequence);
        console.error('Balance Sheet saved; refresh or validation failed:', error);
      } finally {
        refreshingPeriods.current.delete(selectedPeriodId);
      }
    },
    [lineItemMap, defaultPeriods, queryClient, refetch, validatePeriod, beginValidation, validationError, upsertValueMutation, entityRecordId]
  );

  // Delete custom row handler
  const handleDeleteRow = useCallback(
    async (customLineItemId: string) => {
      if (!entityRecordId || !periodId) return;
      if (window.confirm('Delete this custom line item? This removes it from all periods.')) {
        defaultPeriods.forEach(period => refreshingPeriods.current.add(period.periodId));
        const sequence = beginValidation(periodId);
        try {
          await deleteCustomMutation.mutateAsync({
            id: customLineItemId,
            entityRecordId,
            statementType: 'balance_sheet',
          });
          await refetch();
          defaultPeriods.forEach(period => refreshingPeriods.current.delete(period.periodId));
          await validatePeriod(periodId, sequence);
        } catch {
          validationError(periodId, sequence);
        } finally {
          defaultPeriods.forEach(period => refreshingPeriods.current.delete(period.periodId));
        }
      }
    },
    [deleteCustomMutation, refetch, entityRecordId, periodId, defaultPeriods, beginValidation, validatePeriod, validationError]
  );

  // Handle per-section "Add Row" — opens custom line item modal with correct category
  const handleAddRow = useCallback(
    (sectionId: string) => {
      const customCat = CATEGORY_TO_CUSTOM[sectionId];
      if (customCat && entityRecordId) {
        setAddDialogCategory(customCat);
        const labelMap: Record<string, string> = {
          current_assets: 'Current Assets',
          non_current_assets: 'Non-Current Assets',
          current_liabilities: 'Current Liabilities',
          non_current_liabilities: 'Non-Current Liabilities',
          equity: 'Equity',
        };
        setAddDialogCategoryLabel(labelMap[sectionId] || sectionId);
        setAddDialogOpen(true);
      }
    },
    [entityRecordId]
  );

  // ========================================================================
  // Build rows from BS_STRUCTURE for a single input category
  // ========================================================================

  const buildCategoryRows = useCallback(
    (entry: BSCategoryConfig): SpreadsheetRow[] => {
      const rows: SpreadsheetRow[] = [];

      if (entry.subcategories) {
        for (const sub of entry.subcategories) {
          // Subcategory header row
          rows.push({
            id: `subheader_${sub.key}`,
            label: sub.label,
            type: 'subheader',
            bold: true,
            values: zeroValues(),
          });
          // Item rows within subcategory
          for (const item of sub.items) {
            rows.push({
              id: item.id,
              label: item.label,
              type: 'input',
              values: buildValuesMap(item.id),
            });
          }
        }
      }

      if (entry.items) {
        for (const item of entry.items) {
          rows.push({
            id: item.id,
            label: item.label,
            type: 'input',
            values: buildValuesMap(item.id),
          });
        }
      }

      // Append custom line items for this category
      const customKey = CATEGORY_TO_CUSTOM[entry.key];
      if (customKey) {
        rows.push(...buildCustomRows(customKey));
      }

      return rows;
    },
    [buildValuesMap, buildCustomRows, zeroValues]
  );

  // ========================================================================
  // Build spreadsheet configuration
  // ========================================================================

  const spreadsheetConfig: SpreadsheetConfig = useMemo(() => {
    const isSMSF = financialTemplate === 'smsf';

    // ---- SMSF Fallback ----
    if (isSMSF) {
      const smsfAssets: SpreadsheetSection = {
        id: 'smsf_assets',
        label: 'SMSF ASSETS',
        collapsible: false,
        rows: SMSF_ITEMS.filter((i) => i.id !== 'loan_balance_smsf').map((item) => ({
          id: item.id,
          label: item.label,
          type: 'input' as const,
          values: buildValuesMap(item.id),
        })),
        totalRow: {
          id: 'total_assets',
          label: 'Total SMSF Assets',
          type: 'total',
          bold: true,
          separator: 'above',
          formula: () => 0,
          values: (() => {
            const values: Record<string, number> = {};
            defaultPeriods.forEach((period) => {
              values[period.periodId] = SMSF_ITEMS
                .filter((i) => i.id !== 'loan_balance_smsf')
                .reduce((sum, item) => sum + getValue(item.id, period.periodId), 0);
            });
            return values;
          })(),
        },
      };

      const smsfLiabilities: SpreadsheetSection = {
        id: 'smsf_liabilities',
        label: 'SMSF LIABILITIES',
        collapsible: false,
        rows: [
          {
            id: 'loan_balance_smsf',
            label: 'Loan Balance (SMSF)',
            type: 'input' as const,
            values: buildValuesMap('loan_balance_smsf'),
          },
        ],
        totalRow: {
          id: 'total_liabilities',
          label: 'Total SMSF Liabilities',
          type: 'total',
          bold: true,
          separator: 'above',
          formula: () => 0,
          values: (() => {
            const values: Record<string, number> = {};
            defaultPeriods.forEach((period) => {
              values[period.periodId] = getValue('loan_balance_smsf', period.periodId);
            });
            return values;
          })(),
        },
      };

      return {
        sections: [smsfAssets, smsfLiabilities],
        periods: defaultPeriods,
        onCellChange: handleCellChange,
        mode: modeProp,
        readOnly,
      };
    }

    // Shared category lookups (needed by both simplified and detailed modes)
    const inputCategories = BS_STRUCTURE.filter(
      (e): e is BSCategoryConfig => e.type === 'input'
    );

    // ---- Simplified Mode ----
    if (modeProp === 'simplified') {
      const simplifiedRows = getSimplifiedBSRows();
      const simplifiedSection: SpreadsheetSection = {
        id: 'bs_simplified',
        label: 'BALANCE SHEET',
        collapsible: false,
        rows: simplifiedRows.map((row) => ({
          id: row.key,
          label: row.label,
          type: 'calculated' as const,
          bold: true,
          values: buildTotalValues(row.key),
        })),
      };

      return {
        sections: [simplifiedSection],
        periods: defaultPeriods,
        onCellChange: handleCellChange,
        mode: modeProp,
        readOnly: true, // Simplified mode is read-only (calculated values)
      };
    }

    // ---- Detailed Mode ----
    // Build sections from BS_STRUCTURE input categories (inputCategories defined above)

    const sections: SpreadsheetSection[] = inputCategories.map((category) => ({
        id: category.key,
        label: category.label.toUpperCase(),
        collapsible: false,
        rows: buildCategoryRows(category),
        totalRow: {
          id: `total_${category.key}`,
          label: `Total ${category.label}`,
          type: 'subtotal' as const,
          bold: true,
          separator: 'above',
          formula: values => calculateGridTotal(`total_${category.key}`, values),
          values: buildTotalValues(`total_${category.key}`),
        },
    }));

    // Grand totals from the shared validation totals.

    const grandTotalsSection: SpreadsheetSection = {
      id: 'grand_totals',
      label: 'TOTALS',
      collapsible: false,
      rows: [
        {
          id: 'total_assets',
          label: 'TOTAL ASSETS',
          type: 'total' as const,
          bold: true,
          values: buildTotalValues('total_assets'),
        },
        {
          id: 'total_liabilities',
          label: 'TOTAL LIABILITIES',
          type: 'total' as const,
          bold: true,
          values: buildTotalValues('total_liabilities'),
        },
        {
          id: 'total_equity',
          label: 'TOTAL EQUITY',
          type: 'total' as const,
          bold: true,
          values: buildTotalValues('total_equity'),
        },
      ],
    };

    // Balance check section
    const balanceCheckSection: SpreadsheetSection = {
      id: 'balance_check',
      label: 'BALANCE CHECK',
      collapsible: false,
      rows: [],
      totalRow: {
        id: 'balance_check',
        label: 'Balance Variance',
        type: 'total',
        bold: true,
        separator: 'above',
        formula: values => calculateGridTotal('balance_check', values),
        values: buildTotalValues('balance_check'),
      },
    };

    return {
      sections: [...sections, grandTotalsSection, balanceCheckSection],
      periods: defaultPeriods,
      onCellChange: handleCellChange,
      mode: modeProp,
      readOnly,
      onAddRow: !readOnly && entityRecordId ? handleAddRow : undefined,
      onDeleteRow: !readOnly ? handleDeleteRow : undefined,
    };

  }, [
    lineItems, lineItemMap, defaultPeriods, modeProp, readOnly,
    handleCellChange, handleDeleteRow, handleAddRow, financialTemplate,
    buildValuesMap, zeroValues, buildCategoryRows, buildTotalValues, calculateGridTotal,
    getValue, buildCustomRows, customDefinitions, customValues, entityRecordId,
  ]);

  // Manual validation trigger
  const handleValidate = useCallback(async () => {
    if (!periodId) return;
    if (readFailed) {
      refreshingPeriods.current.add(periodId);
      const sequence = beginValidation(periodId);
      try {
        await refetch();
        await validatePeriod(periodId, sequence);
      } catch {
        validationError(periodId, sequence);
      } finally {
        refreshingPeriods.current.delete(periodId);
      }
      return;
    }
    await validatePeriod(periodId);
  }, [periodId, readFailed, refetch, validatePeriod, beginValidation, validationError]);

  // One automatic attempt per source input, including failures. Saves reserve their own request.
  useEffect(() => {
    if (periodId && !isLoading && !readFailed && (lineItems.length > 0 || customDefinitions.length > 0) && !refreshingPeriods.current.has(periodId) && !attemptedInputs.current.has(inputKey)) {
      void handleValidate();
    }
  }, [periodId, inputKey, lineItems.length, customDefinitions.length, handleValidate, readFailed, isLoading]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RotateCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!periodId) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <p className="text-sm text-gray-500">No period selected</p>
      </div>
    );
  }

  const periodLabel = defaultPeriods.find(period => period.periodId === periodId)?.label ?? periodId;
  const totalsUnavailable = totalsByPeriod[periodId] === null;
  const unrepresentedLines = hasUnrepresentedBalanceSheetLines(lineItems);
  const balanceVariance = validation
    ? validation.discrepancy
    : 0;
  const isBalanced = validation?.isBalanced ?? false;

  return (
    <Card data-testid="balance-sheet-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle>Balance Sheet</CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleValidate}
            disabled={isValidating}
          >
            <RotateCw className={`h-4 w-4 mr-2 ${isValidating ? 'animate-spin' : ''}`} />
            Validate
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {defaultPeriods.filter(period => !readFailed && totalsByPeriod[period.periodId] === null).map(period => (
          <p role="status" key={period.periodId}>{period.label}: totals are too large to calculate. The figures can still be edited.</p>
        ))}
        {!readFailed && unrepresentedLines && <p role="status">{periodLabel}: Balance Sheet could not be confirmed because some figures are not in this grid.</p>}
        {defaultPeriods.filter((_, index) => bsQueries[index].isError || customQuery.isError).map(period => (
          <p role="status" key={period.periodId}>{period.label}: Balance Sheet could not be validated because the figures could not be loaded. You can try Validate again.</p>
        ))}
        {!readFailed && (validationFailed || totalsUnavailable) && <p role="status">{periodLabel}: Balance Sheet could not be validated. You can try Validate again.</p>}
        {validation && !readFailed && !totalsUnavailable && !unrepresentedLines && (
          <Alert
            className={`mb-4 ${isBalanced ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
            data-testid="balance-check-row"
          >
            <div className="flex items-start gap-2">
              {isBalanced ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <div>
                <p className="font-semibold">
                  {`${periodLabel}: ${isBalanced ? 'Balance Sheet is Balanced' : 'Balance Sheet Unbalanced'}`}
                </p>
                <AlertDescription>
                  {`$${Math.abs(balanceVariance).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} variance`}
                </AlertDescription>
              </div>
            </div>
          </Alert>
        )}

        {!readFailed && <FinancialSpreadsheet {...spreadsheetConfig} sections={spreadsheetConfig.sections.map(section => ({
          ...section,
          rows: section.rows.map(row => ({ ...row, unavailableText: 'Too large to calculate' })),
          totalRow: section.totalRow ? { ...section.totalRow, unavailableText: 'Too large to calculate' } : undefined,
        }))} />}
      </CardContent>

      {entityRecordId && (
        <AddCustomLineItemDialog
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          entityRecordId={entityRecordId}
          statementType="balance_sheet"
          defaultCategory={addDialogCategory}
          categoryLabel={addDialogCategoryLabel}
        />
      )}
    </Card>
  );
};
